// ===========================================
// COMPONENTE: Matriz de Permisos (editable por rol — capa org-rol)
// ===========================================

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Check, X, Lock } from 'lucide-react'
import { toast } from 'sonner'
import {
  aplicarRequeridos,
  PERMISOS_POR_ROL,
  ROL_CONFIG,
  FLAGS_META_NO_CONCEDIBLES,
  type Rol,
  type Permisos,
} from '@/types/roles'
import { usePermissions } from '@/hooks/usePermissions'
import { useOverridesRol, useActualizarPermisosRol } from '@/hooks/useUsuarios'
import { CATEGORIAS_PERMISOS, PERMISO_LABELS, etiquetaConCategoria } from './permisosCatalogo'

const ROLES: Rol[] = ['contador_principal', 'contador_asociado', 'asistente', 'solo_lectura']

// Roles cuya política puede ajustarse por organización (override org-rol).
// contador_principal (owner/admin) = acceso total fijo, no editable.
type RolEditable = 'contador_asociado' | 'asistente' | 'solo_lectura'
const ROLES_EDITABLES: RolEditable[] = ['contador_asociado', 'asistente', 'solo_lectura']

const esMeta = (flag: keyof Permisos): boolean =>
  FLAGS_META_NO_CONCEDIBLES.includes(flag)

const esRolEditable = (rol: Rol): rol is RolEditable =>
  (ROLES_EDITABLES as Rol[]).includes(rol)

export function MatrizPermisos() {
  const { puede } = usePermissions()
  const { data: overrides = [] } = useOverridesRol()
  const actualizarRol = useActualizarPermisosRol()

  const puedeEditar = puede.gestionarUsuarios

  // Mapa rol -> flag -> permitido (overrides vigentes de la organización)
  const overrideMap = new Map<string, Map<string, boolean>>()
  overrides.forEach((o) => {
    if (!overrideMap.has(o.rol)) overrideMap.set(o.rol, new Map())
    overrideMap.get(o.rol)!.set(o.flag, o.permitido)
  })

  const efectivo = (rol: Rol, flag: keyof Permisos): boolean =>
    overrideMap.get(rol)?.get(flag) ?? PERMISOS_POR_ROL[rol][flag]

  const tieneOverride = (rol: Rol, flag: keyof Permisos): boolean =>
    overrideMap.get(rol)?.has(flag) ?? false

  const onToggle = (rol: RolEditable, flag: keyof Permisos) => {
    const flags = Object.keys(PERMISOS_POR_ROL[rol]) as (keyof Permisos)[]
    const actuales: Partial<Record<keyof Permisos, boolean>> = Object.fromEntries(flags.map((f) => [f, efectivo(rol, f)]))
    // ORR-626: el flag tocado arrastra el que requiere o los que dependen de él, y van en una sola
    // llamada porque el writer valida el rol completo.
    const { valores, arrastrados } = aplicarRequeridos(actuales, flag, !actuales[flag])
    // Si el nuevo valor coincide con la base del rol, se limpia el override (null)
    const cambios: Record<string, boolean | null> = Object.fromEntries(
      [flag, ...arrastrados].map((f) => {
        const valor = valores[f] === true
        return [f, valor === PERMISOS_POR_ROL[rol][f] ? null : valor]
      })
    )
    actualizarRol.mutate(
      { rol, cambios },
      {
        onSuccess: () =>
          toast.success(
            arrastrados.length > 0
              ? `Permiso del rol actualizado. También cambió: ${arrastrados.map(etiquetaConCategoria).join(', ')}`
              : 'Permiso del rol actualizado'
          ),
        onError: (e) =>
          toast.error(e instanceof Error ? e.message : 'Error al actualizar el permiso'),
      }
    )
  }

  return (
    <div className="space-y-3">
      {puedeEditar && (
        <p className="text-sm text-muted-foreground">
          Tocá una celda de Asociado, Asistente o Solo Lectura para conceder o quitar ese permiso
          al rol en tu organización. Un permiso que requiere otro lo concede junto, y quitar el
          requerido quita los que dependen de él. El borde ámbar indica un ajuste propio de la organización.
          Los permisos con candado no se pueden delegar.
        </p>
      )}
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[150px]">Modulo</TableHead>
              <TableHead className="w-[120px]">Permiso</TableHead>
              {ROLES.map((rol) => (
                <TableHead key={rol} className="text-center w-[120px]">
                  <Badge variant="outline" className={ROL_CONFIG[rol].bgClass}>
                    {ROL_CONFIG[rol].label}
                  </Badge>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Object.entries(CATEGORIAS_PERMISOS).map(([key, categoria]) =>
              categoria.permisos.map((permiso, idx) => (
                <TableRow key={`${key}-${permiso}`}>
                  {idx === 0 && (
                    <TableCell
                      rowSpan={categoria.permisos.length}
                      className="font-medium bg-muted/50 align-top"
                    >
                      {categoria.label}
                    </TableCell>
                  )}
                  <TableCell className="text-sm">{PERMISO_LABELS[permiso]}</TableCell>
                  {ROLES.map((rol) => {
                    const meta = esMeta(permiso)
                    const val = efectivo(rol, permiso)
                    const override = tieneOverride(rol, permiso)
                    const editable = puedeEditar && esRolEditable(rol) && !meta

                    return (
                      <TableCell key={`${rol}-${permiso}`} className="text-center">
                        {meta ? (
                          <Lock
                            className="h-4 w-4 text-muted-foreground/60 mx-auto"
                            aria-label="No delegable"
                          />
                        ) : editable ? (
                          <button
                            type="button"
                            onClick={() => onToggle(rol as RolEditable, permiso)}
                            disabled={actualizarRol.isPending}
                            title={
                              override
                                ? 'Ajustado por la organización (clic para volver al valor por defecto)'
                                : 'Valor por defecto del rol (clic para cambiarlo)'
                            }
                            className={`mx-auto flex h-6 w-6 items-center justify-center rounded transition-colors hover:bg-muted disabled:opacity-50 ${
                              override ? 'ring-1 ring-amber-400' : ''
                            }`}
                          >
                            {val ? (
                              <Check className="h-4 w-4 text-green-600" />
                            ) : (
                              <X className="h-4 w-4 text-red-400" />
                            )}
                          </button>
                        ) : val ? (
                          <Check className="h-4 w-4 text-green-600 mx-auto" />
                        ) : (
                          <X className="h-4 w-4 text-red-400 mx-auto" />
                        )}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

// Componente simplificado para mostrar en card
export function ResumenPermisos({ rol }: { rol: Rol }) {
  const permisos = PERMISOS_POR_ROL[rol]
  const config = ROL_CONFIG[rol]

  const categoriasActivas = Object.entries(CATEGORIAS_PERMISOS)
    .filter(([, cat]) => cat.permisos.some((p) => permisos[p]))
    .map(([, cat]) => cat.label)

  return (
    <div className="space-y-2">
      <Badge variant="outline" className={config.bgClass}>
        {config.label}
      </Badge>
      <p className="text-sm text-muted-foreground">{config.description}</p>
      <div className="flex flex-wrap gap-1 mt-2">
        {categoriasActivas.map((cat) => (
          <Badge key={cat} variant="secondary" className="text-xs">
            {cat}
          </Badge>
        ))}
      </div>
    </div>
  )
}
